import { DocumentPlusIcon } from '@heroicons/react/24/outline';
import React from 'react';

import { DiscriminatedTreatmentRequestCreatedEvent } from '@/services/api/generated/matterChangesService';
import { useGetMedicalProvidersQuery } from '@/services/api/treatmentService';

import {
  ActivityActions,
  ActivityBody,
  ActivityDescription,
  ActivityIcon,
  ActivityItem,
  ActivityLink,
  ActivityTitle,
  CollapsibleList,
} from '../activity-feed-v2';
import { getUserFromEvent } from '../activity-feed-v2/ActivityDiffList';
import { formatEnumValue } from '../activity-feed-v2/utils';

export interface TreatmentRequestCreatedEventProps {
  event: DiscriminatedTreatmentRequestCreatedEvent;
}

interface TreatmentRequestDetailField {
  label: string;
  value: string | number | boolean | null | undefined;
}

export const TreatmentRequestCreatedEvent: React.FC<
  TreatmentRequestCreatedEventProps
> = ({ event }) => {
  const { treatment_request } = event.payload;
  const user = getUserFromEvent(event);
  const { data: providers } = useGetMedicalProvidersQuery(event.matter_id);

  const provider = providers?.find(
    (p) => p.id === treatment_request.provider_id,
  );

  const icon = (
    <ActivityIcon
      icon={
        <div className="flex h-6 w-6 items-center justify-center rounded-full bg-indigo-100">
          <DocumentPlusIcon className="h-4 w-4 text-indigo-600" />
        </div>
      }
    />
  );

  const treatmentRequestDetailFields: TreatmentRequestDetailField[] = [
    {
      label: 'Certified',
      value:
        treatment_request?.is_certified !== undefined
          ? treatment_request.is_certified
            ? 'Yes'
            : 'No'
          : null,
    },
    {
      label: 'Urgent',
      value:
        treatment_request?.is_urgent !== undefined
          ? treatment_request.is_urgent
            ? 'Yes'
            : 'No'
          : null,
    },
  ];

  const providedRecords = treatment_request?.provided_records
    ? treatment_request.provided_records.map((r) => formatEnumValue(r))
    : [];

  const detailItems = treatmentRequestDetailFields
    .filter(
      ({ value }) => value !== null && value !== undefined && value !== '',
    )
    .map(({ label, value }) => `${label}: ${value}`);

  const body = (
    <ActivityBody>
      {provider?.name && <ActivityTitle>{provider.name}</ActivityTitle>}
      {treatment_request?.status && (
        <div>
          <ActivityDescription>
            {providedRecords.join(', ')}
          </ActivityDescription>
          <ActivityDescription>
            Status: {formatEnumValue(treatment_request.status)}
          </ActivityDescription>
        </div>
      )}
      {detailItems.length > 0 && (
        <CollapsibleList
          items={detailItems}
          showLabel="Show Details"
          hideLabel="Hide Details"
        />
      )}
    </ActivityBody>
  );

  const actions = event.payload.treatment_request?.provider_id && (
    <ActivityActions>
      <ActivityLink
        href={`/matters/${event.matter_id}/treatment/${event.payload.treatment_request.provider_id}`}
      >
        View Provider
      </ActivityLink>
    </ActivityActions>
  );

  return (
    <ActivityItem
      icon={icon}
      title="Treatment Request Created"
      timestamp={new Date(event.occurred_at ?? new Date())}
      user={user}
      body={body}
      actions={actions}
    />
  );
};
